/* eslint-disable no-use-before-define */
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Link,
  useMediaQuery,
  useTheme,
  FormHelperText,
} from "@mui/material";
import Lockey from "../../../../assets/icons/LockKey.svg";
import { useSelector } from "react-redux";
import { getCurrentFormPhase } from "../../../../utilis/common-funtions";

const VerifyOTPModal = ({
  open,
  onClose,
  onVerify,
  isBlocked,
  handleResend,
  errorMessage,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60); // ⏱ countdown in seconds
  const inputsRef = useRef([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
 const {  applicantData } = useSelector((state) => state.voucherForm);

  // Reset OTP + timer on open
  useEffect(() => {
    if (open) {
      setOtp(["", "", "", "", "", ""]);
      setTimer(60); // restart countdown
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    }
  }, [open]);

  // Countdown effect
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = useCallback(() => onVerify(otp.join("")), [onVerify, otp]);

  useEffect(() => {
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !isBlocked) {
      handleVerify();
    }
  };
  window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [isBlocked, handleVerify ]);

  const handleResendClick = () => {
    handleResend();
    setTimer(60); // restart countdown after resend
  };
 const handleOTPPaste = (e, startIndex) => {
  e.preventDefault();
  const pastedData = e.clipboardData.getData("text").trim();

  // Only allow digits
  if (!/^\d+$/.test(pastedData)) return;

  const digits = pastedData.split("").slice(0, 6); // max 6
  const newOtp = [...otp];

  digits.forEach((digit, i) => {
    const index = startIndex + i;
    if (index < 6) {
      newOtp[index] = digit;
    }
  });

  setOtp(newOtp);

  // focus next empty field
  const nextIndex = startIndex + digits.length;
  if (nextIndex < 6 && inputsRef.current[nextIndex]) {
    inputsRef.current[nextIndex].focus();
  }
};

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          width: isMobile ? "90%" : "440px",
          maxWidth: isMobile ? "90%" : "440px",
          borderRadius: "16px",
          padding: isMobile ? "16px" : "24px",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 0, textAlign: "center", mb: 1, fontSize: '14px' }}>
        Verify your identity
        {/* <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton> */}
        <Typography sx={{ fontSize: '12px', textAlign: 'center', color: 'text.secondary' }}>
          We&apos;ve sent a 6-digit code to {applicantData?.applicant1?.personalDetails?.emailAddress || applicantData?.applicant1?.personalDetails?.email}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", padding: "0px 8px" }}>
        <Typography sx={{fontSize: '12px', mb: 2, color: 'red'}}>
          {errorMessage}
        </Typography>

        {/* OTP Inputs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: isMobile ? "8px" : "20px",
            mb: 3,
          }}
        >
          {otp?.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputsRef.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
             onPaste={(e) => handleOTPPaste(e, index)}
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center", fontSize: isMobile ? 18 : 20 },
              }}
              variant="outlined"
              sx={{
                width: isMobile ? "40px" : "52px",
                height: isMobile ? "44px" : "54px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "100%",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.1)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(26, 64, 125, 1)",
                  },
                },
              }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <Button
          variant="contained"
          fullWidth
          disabled={isBlocked}
          sx={{
            backgroundColor: isBlocked ? "#ccc" : "rgba(26, 64, 125, 1)",
            color: "#ffffff",
            padding: "12px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": {
              backgroundColor: isBlocked ? "#ccc" : "rgba(26, 64, 125, 1)",
            },
          }}
          onClick={handleVerify}
        >
          Verify
        </Button>
        {isBlocked && !errorMessage && (
          <FormHelperText error sx={{ textAlign: "center", mt: 1, fontSize: '12px' }}>
            You have entered an invalid OTP 5 times. Please try again later.
          </FormHelperText>
        )}

        {/* Resend Link with Timer */}
        <Typography sx={{ fontSize: '12px', mt: 2, mb: 2 }}>
          Don’t have a code?{" "}
          {timer > 0 ? (
            <span style={{ color: "#999" }}>Resend in {timer}s</span>
          ) : (
            <Link
              sx={{
                fontSize: '12px',
                color: "rgba(26, 64, 125, 1)",
                textDecoration: "none",
                fontWeight: "600",
                marginLeft: "5px",
                cursor: "pointer",
              }}
              onClick={handleResendClick}
            >
              Resend code
            </Link>
          )}
        </Typography>

        {/* Security Note */}
        <Box
          sx={{
            display: "flex",
            mt: 2,
            p: 1,
            backgroundColor: "#fff3cd",
            color: "#856404",
            borderRadius: "8px",
            justifyContent: "start",
            alignItems: "center",
          }}
        >
          <img
            src={Lockey}
            alt="lockey"
            style={{ width: "16px", height: "16px", marginRight: "5px" }}
          />
          <Typography sx={{ fontSize: "8px" }}>
            This verification is required for security purposes when accessing
            your {getCurrentFormPhase(applicantData?.formPhase)} details
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyOTPModal;